import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import emailjs from "@emailjs/browser";

export default function Contact() {
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();

  const templateID = process.env.REACT_APP_EMAIL_TEMPLATE_ID;
  const serviceID = process.env.REACT_APP_EMAIL_SERVICE_ID;
  const publicKey = process.env.REACT_APP_EMAIL_PUBLIC_KEY;

  function validate(form) {
    const errs = {};
    if (!form.name.value.trim()) errs.name = true;
    if (!form.email.value.trim() || !/\S+@\S+\.\S+/.test(form.email.value)) errs.email = true;
    if (!form.company.value.trim()) errs.company = true;
    if (!form.subject.value.trim()) errs.subject = true;
    if (!form.message.value.trim()) errs.message = true;
    return errs;
  }

  function sendEmail(e) {
    e.preventDefault();

    const errs = validate(e.target);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus('sending');

    const templateParams = {
      name: e.target.name.value,
      email: e.target.email.value,
      company: e.target.company.value,
      subject: e.target.subject.value,
      message: e.target.message.value,
    };

    emailjs
      .send(serviceID, templateID, templateParams, { publicKey: publicKey })
      .then(() => {
        setStatus('success');
        e.target.reset();
        setTimeout(() => setStatus('idle'), 5000);
      })
      .catch(() => {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      });
  }

  const inputClass = (field) =>
    `form-input ${errors[field] ? 'border-red-400 ring-1 ring-red-400' : ''}`;

  return (
    <section
      className="relative py-20 bg-white"
      id="contact"
    >
      <div className="container">
        <div className="grid grid-cols-1 pb-8 text-center">
          <h1 className="t-h1">{t("Contact.contactUs")}</h1>
          <h2 className="t-h2">{t("Contact.improve")}</h2>
          <p className="text-slate-400 dark:text-slate-300 max-w-xl mx-auto">
            {t("Contact.tellUs")}
          </p>
        </div>

        {/* Success banner */}
        {status === 'success' && (
          <div className="mb-6 p-4 bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-800 rounded-xl text-teal-700 dark:text-teal-300 text-center font-medium">
            {t("Contact.successMessage")}
          </div>
        )}

        {/* Error banner */}
        {status === 'error' && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-center font-medium">
            {t("Contact.errorMessage")}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 md:grid-cols-2 mt-8 items-center gap-6">
          <div className="lg:col-span-8">
            <div className="p-6 rounded-md shadow bg-white dark:bg-slate-900">
              <form onSubmit={sendEmail} noValidate>
                <div className="grid lg:grid-cols-12 lg:gap-6">
                  <div className="lg:col-span-6 mb-5">
                    <input
                      name="name"
                      id="name"
                      type="text"
                      className={inputClass('name')}
                      placeholder={t("Contact.name")}
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{t("Contact.name")}</p>}
                  </div>

                  <div className="lg:col-span-6 mb-5">
                    <input
                      name="email"
                      id="email"
                      type="email"
                      className={inputClass('email')}
                      placeholder={t("Contact.email")}
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">Email</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1">
                  <div className="mb-5">
                    <input
                      name="company"
                      id="company"
                      className={inputClass('company')}
                      placeholder={t("Contact.company")}
                    />
                  </div>
                  <div className="mb-5">
                    <input
                      name="subject"
                      id="subject"
                      className={inputClass('subject')}
                      placeholder={t("Contact.subject")}
                    />
                  </div>

                  <div className="mb-5">
                    <textarea
                      name="message"
                      id="message"
                      className={`${inputClass('message')} textarea h-28`}
                      placeholder={t("Contact.message")}
                    ></textarea>
                  </div>
                </div>
                <button
                  type="submit"
                  id="submit"
                  name="send"
                  disabled={status === 'sending'}
                  className="btn rounded-md h-11 justify-center flex items-center gap-2 font-semibold disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                  style={{ background: '#00C9B1', color: '#010C66' }}
                >
                  {status === 'sending' ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {t("Contact.sending")}
                    </>
                  ) : (
                    t("Contact.sendMessage")
                  )}
                </button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="lg:ms-8">
              <div className="flex">
                <div className="icons text-center mx-auto">
                  <i className="uil uil-phone block rounded text-2xl dark:text-white mb-0"></i>
                </div>
                <div className="flex-1 ms-6">
                  <h5 className="text-lg dark:text-white mb-2 font-medium">
                    {t("Contact.phone")}
                  </h5>
                  <a href="tel:+59892516901" className="text-teal-500">
                    +598 92-516-901
                  </a>
                </div>
              </div>
              <div className="flex mt-4">
                <div className="icons text-center mx-auto">
                  <i className="uil uil-envelope block rounded text-2xl dark:text-white mb-0"></i>
                </div>
                <div className="flex-1 ms-6">
                  <h5 className="text-lg dark:text-white mb-2 font-medium">
                    Email
                  </h5>
                  <a href="mailto:contact@analabs.tech" className="text-teal-500">
                    contact@analabs.tech
                  </a>
                </div>
              </div>
              <div className="flex mt-4">
                <div className="icons text-center mx-auto">
                  <i className="uil uil-map-marker block rounded text-2xl dark:text-white mb-0"></i>
                </div>
                <div className="flex-1 ms-6">
                  <h5 className="text-lg dark:text-white mb-2 font-medium">
                    {t("Contact.location")}
                  </h5>
                  <p className="text-teal-500 mb-2">
                    {t("Contact.locationInfo")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
